import { useEffect, useMemo, useState } from "react";

/**
 * Circular readiness gauge.
 * - score: 0..100
 * - status: 'ready' | 'attention' | 'critical'
 * - size: base pixel size. On narrow viewports (< 480px) we shrink to
 *         120px automatically so the tile doesn't overrun the phone
 *         gutter.
 */
export default function ReadinessGauge({ score = 0, status = "ready", size = 156 }) {
  const [effSize, setEffSize] = useState(size);
  useEffect(() => {
    const update = () => {
      // < 480px = phone → 120; < 640px = large phone → 140; else base.
      const w = window.innerWidth;
      if (w < 480) setEffSize(Math.min(120, size));
      else if (w < 640) setEffSize(Math.min(140, size));
      else setEffSize(size);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [size]);

  const stroke = effSize >= 150 ? 12 : 10;
  const r = (effSize - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score));
  const offset = c - (pct / 100) * c;

  const color = useMemo(() => {
    if (status === "ready") return "#4F8A3A";
    if (status === "attention") return "#C8932C";
    return "#C45454";
  }, [status]);

  return (
    <div className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: effSize, height: effSize, flex: `0 0 ${effSize}px` }}
      data-testid="readiness-gauge">
      <svg width={effSize} height={effSize} className="-rotate-90">
        <circle
          cx={effSize / 2} cy={effSize / 2} r={r}
          stroke="var(--vb-bg-2)" strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={effSize / 2} cy={effSize / 2} r={r}
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 600ms ease-out, stroke 200ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="vb-heading" style={{ color: "var(--vb-text-2)", fontSize: effSize >= 150 ? "1.875rem" : "1.5rem", lineHeight: 1 }}>
          {Number.isInteger(pct) ? pct : pct.toFixed(1)}<span style={{ fontSize: "0.7em", opacity: 0.6 }}>%</span>
        </div>
        <div className="text-[10px] uppercase tracking-wider opacity-60 mt-0.5">
          Readiness
        </div>
      </div>
    </div>
  );
}
